// creditGuard — the workspace credit safety surface's data half.
//
// Feeds three UI consumers (tickets/fable.md, Nika 2026-08-13 "beautiful
// workspace-blocker when workspace runs out of credits. also notifications +
// banner to admin when its approaching to end"):
//   • CreditBlocker  — full-viewport stop when the workspace is EXHAUSTED
//   • CreditBanner   — admin corner pill for LOW / CRITICAL, links /credits
//   • threshold notifications — toast for the signed-in admin + persisted
//     bell notifications for the OTHER admins, deduped per workspace+month.
//
// Data: `root._creditGuard` — fetched via `sdk.getCreditBalance(wsId)` (the
// exact call /credits' own balance card makes, pages/admin/usage.js) on boot
// (loadPrivateData fan-out) and on every workspace switch (onWorkspaceChange
// refetch block; the key is in state.js's WORKSPACE_SCOPED_KEYS wipe
// registry, per the workspace-switch isolation invariants). All figures come
// from the server's credit-meter — nothing here hardcodes a grant size,
// price, or credit count (feedback_never_hardcode_use_db_record). The
// LOW/CRITICAL fractions are client UX presentation thresholds (when to
// start warning), not billing-contract values — the same class of constant
// as ProgressBar's warnAt/dangerAt on the usage page.
//
// FA204 / frank functions-file convention: every export below is
// SELF-CONTAINED — no export reads a sibling export or a module-scope
// helper (the frank lift extracts each export individually; a bare sibling
// reference reads as undefined after serialization —
// project_frank_drops_module_local_helpers). The status derivation is
// therefore inlined where needed.
//
// Bare shared-library import + call-time deferred reads — an eager
// module-scope destructure of the barrel's default crashes the shell boot
// inside the shared-barrel init cycle (see workspaceProjects.js).
import _wsShared from '@symbo-ls/workspace-shared'

// creditGuardState(root) → a normalized view of `root._creditGuard`:
//   { loaded, error, status, total, grantSize, grantRemaining, topupRemaining }
// status: 'ok' | 'low' | 'critical' | 'exhausted' | 'unknown' — null until
// loaded. 'unknown' means the balance could not be READ (server returned
// `found: false`, or the total is absent/non-numeric) as opposed to being
// read as zero. Nothing may be gated on 'unknown': it never blocks, never
// warns and never notifies — only a positively-confirmed value does.
// Anchoring: LOW/CRITICAL are fractions of the monthly grant (30% / 10%
// remaining — the usage page's ProgressBar warns at 70%/90% used, the same
// boundaries seen from the other side). Top-up credits count toward
// `total`, so a workspace with an empty grant but purchased credits is NOT
// low. When the server reports no grant size (grantSize 0 —
// enterprise/custom pools), only EXHAUSTED is decidable and the ratio
// levels stay silent rather than guessing.
export const creditGuardState = function (root) {
  const r = root || this?.state?.root
  const raw = r?._creditGuard
  if (!raw) return { loaded: false, error: null, status: null }
  const data = raw.data || raw
  if (data.error) return { loaded: true, error: data.error, status: null }
  // UNKNOWN ≠ ZERO (CREDIT-BLOCK-1 / FALSE-ZERO-1). The server marks a
  // balance it could not resolve with `found: false` + NULL numerics
  // (CreditsService.getWorkspaceBalance). The coercion MUST sit behind that
  // discriminator, not in front of it: `null ?? 0` evaluates to `0`, so the
  // old `Number(data.total ?? 0)` turned both a null total AND an absent
  // field into a confident zero — which became `exhausted`, which became a
  // full-viewport lockout on a workspace holding 1.25M credits. A balance
  // we cannot read is `'unknown'`, and nothing may be gated on it.
  const rawTotal = data.total
  const totalNum = Number(rawTotal)
  if (data.found === false || rawTotal == null || rawTotal === '' || !Number.isFinite(totalNum)) {
    return {
      loaded: true,
      error: null,
      status: 'unknown',
      total: null,
      grantSize: null,
      grantRemaining: null,
      topupRemaining: null
    }
  }
  const total = totalNum
  const grantSize = Number(data.grantSize ?? 0)
  const grantRemaining = Number(data.grantRemaining ?? 0)
  const topupRemaining = Number(data.topupRemaining ?? 0)
  let status = 'ok'
  if (total <= 0) status = 'exhausted'
  else if (grantSize > 0 && total <= grantSize * 0.1) status = 'critical'
  else if (grantSize > 0 && total <= grantSize * 0.3) status = 'low'
  return { loaded: true, error: null, status, total, grantSize, grantRemaining, topupRemaining }
}

// creditBlockerShown(el, root, seed) → is the out-of-credits NOTICE up?
//
// THE one source of truth for that question, because TWO props of the same
// element need it and they must never disagree: `CreditBlocker.Overlay.hide`
// (what paints) and `CreditBlocker.Overlay.inertOutside` (what the page behind
// may do while it paints). The notice is a modal LAYER — a layer that is up
// must hold the page behind inert, and a layer that is hidden must not (see
// the Overlay's own note) — so both read this and nothing else.
//
// Up when: signed in; not a white-label non-member behind the request-access
// gate (their personal workspace's balance is unrelated noise there); not on a
// recovery/identity route (the credit page itself, sign-in, auth, profile,
// account — nobody is locked out of their own account chrome); not dismissed
// for this session (root state, or the per-workspace sessionStorage marker a
// remount within the tab reads back); and the balance is POSITIVELY read as
// zero — `unknown` and a metering error never gate (CREDIT-BLOCK-1).
//
// `seed` true (the paint path only): a dismissal found in sessionStorage is
// written into ROOT STATE too, so CreditBanner's pill — which reads
// `_creditBlockerDismissed` directly — sees the same truth. The layer path
// passes false: a prop that decides modality writes nothing.
//
// Self-contained per this file's FA204 convention.
export const creditBlockerShown = function (el, root, seed) {
  const e = el || this
  const r = root || e?.state?.root
  if (!e || !r || !r.signedIn) return false
  if (e.call('isWhitelabelNonMemberGateActive', r)) return false
  // Scheme-A URLs carry the /w/<org>/<workspace> prefix — strip it so the
  // allowlist matches the ROUTE, not the tenancy prefix.
  const path = e.call('stripWorkspacePrefix', r.currentPath || '/') || '/'
  if (
    path.startsWith('/admin/usage') ||
    path.startsWith('/login') ||
    path.startsWith('/auth') ||
    path.startsWith('/profile') ||
    path.startsWith('/account')
  ) {
    return false
  }
  if (r._creditBlockerDismissed) return false
  // `_creditBlockerDismissed` is in-memory root state, so ANY remount wiped it
  // and re-armed the gate on every page mount (CREDITS-GATE-UI-BREAK-1); the
  // sessionStorage marker survives a remount within the tab and dies with it.
  const wsId = r.workspace?.id || r.workspace?._id || null
  if (wsId && e.call('readCreditBlockerDismissed', wsId)) {
    if (seed && typeof r.update === 'function') {
      r.update({ _creditBlockerDismissed: true }, { preventFetch: true })
    }
    return false
  }
  const guard = e.call('creditGuardState', r)
  // 'unknown' deliberately does NOT reach here — only a positively confirmed
  // zero gates (CREDIT-BLOCK-1 defect #2, fail direction).
  return !!(guard && guard.loaded && !guard.error && guard.status === 'exhausted')
}

// creditShortState(el, root) → { needed, available, admin } | null — the
// SHORTFALL the pill has to show, or null.
//
// A request the server refuses for credits says what it needed and what the
// workspace had ("needed 222, available 100"). The toast that carries it is
// gone in seconds, and the reply in the thread has no button — so
// `notifyCreditsExhaustedFromMessage` also HOLDS that fact in
// `root._creditsShort` (`{ needed, available, at, msgId, dismissed }`), and
// this answers whether it is still the pill's business:
//   • not once the person closed it (`dismissed`) — the record itself stays,
//     because the usage page keeps telling the truth about it;
//   • not at a ZERO balance — an empty workspace is CreditBlocker's (and, once
//     that is dismissed, the exhausted pill's), never a second notice;
//   • not once the balance covers it. The freshest balance we hold decides: the
//     guard when it was read AFTER the refusal, else the `available` the
//     refusal itself reported (a stale guard from boot must not hide a refusal
//     that just happened).
// `available` in the answer is that balance, so the pill never quotes a number
// the workspace no longer has. `admin` picks the way out: an admin adds
// credits, a member asks one.
//
// Self-contained per this file's FA204 convention (see creditPillVisible).
export const creditShortState = function (el, root) {
  const e = el || this
  const r = root || e?.state?.root
  const short = r?._creditsShort
  if (!e || !r || !short || short.dismissed) return null
  const needed = Number(short.needed)
  const reported = Number(short.available)
  if (!Number.isFinite(needed) || !Number.isFinite(reported)) return null
  const guard = e.call('creditGuardState', r)
  if (guard && guard.status === 'exhausted') return null
  const read = Number(r._creditGuard && r._creditGuard._at)
  const total = Number(guard && guard.total)
  const guardIsNewer =
    !!guard &&
    guard.loaded &&
    !guard.error &&
    guard.status !== 'unknown' &&
    Number.isFinite(total) &&
    Number.isFinite(read) &&
    read >= Number(short.at || 0)
  const balance = guardIsNewer ? total : reported
  if (!(balance > 0) || balance >= needed) return null
  return {
    needed,
    available: balance,
    admin: !!e.call('currentUserIsAdmin', r),
    // A member has already asked the admins about THIS refusal.
    asked: !!short.asked
  }
}

// askAdminsForCredits(el, root) → Promise<boolean> — a MEMBER's way out of a
// held shortfall: tell the workspace's admins, in the app, that the person's
// last AI request needs credits the workspace does not have. One notification
// per admin (the bell row the credit thresholds already use, type 'system',
// deep-linked to the credit page), once per refusal (`_creditsShort.asked`).
//
// Honest about what it did: a send counts only when the server returned a row
// for it. Not reachable — a member's roster may hide the admins' addresses —
// the person is told WHO to ask by name (or, with no names either, to ask "a
// workspace admin"), and the button stays a button: nothing is marked asked.
//
// Self-contained per this file's FA204 convention.
export const askAdminsForCredits = async function (el, root) {
  const host = el && el.call ? el : this
  const r = root || host?.state?.root
  if (!r || !host || typeof host.call !== 'function') return false
  const short = r._creditsShort
  if (!short || short.asked) return false
  const t = (key) => String(host.call('polyglot', key))
  const toast = (type, title, message) => {
    try {
      host.call('openNotification', { type, title, message })
    } catch (e) {}
  }
  const fmt = (n) => Number(n || 0).toLocaleString('en-US')
  const byUser = r.roles?.byUser || {}
  const people = Array.isArray(r.people) ? r.people : []
  const admins = people.filter((p) => {
    const role = p?.userId != null ? byUser[p.userId] : null
    return role === 'owner' || role === 'admin'
  })
  const me = r.currentUserEmail
  const adminEmails = admins.map((p) => p.email).filter((email) => email && email !== me)
  if (!adminEmails.length) {
    const names = admins
      .map((p) => p.name)
      .filter(Boolean)
      .slice(0, 3)
      .join(', ')
    toast(
      'error',
      t('workspace.credits.ask.failedTitle'),
      names
        ? t('workspace.credits.ask.whoMessage').replace('{names}', () => names)
        : t('workspace.credits.ask.failedMessage')
    )
    return false
  }
  let who = ''
  try {
    const mine = host.call('currentUser', r)
    who = (mine && mine.name) || ''
  } catch (e) {}
  who = who || me || ''
  const wsName = r.workspace?.name || t('workspace.credits.thisWorkspace')
  const wsId = r.workspace?.id || r.workspace?._id || null
  const body = t('workspace.credits.ask.body')
    .replace('{name}', () => who)
    .replace('{workspace}', () => wsName)
    .replace('{needed}', () => fmt(short.needed))
    .replace('{available}', () => fmt(short.available))
  const sent = await Promise.all(
    adminEmails.map((email) =>
      Promise.resolve(
        host.call('createNotification', {
          userEmail: email,
          type: 'system',
          title: t('workspace.credits.ask.title'),
          body,
          href: '/admin/usage',
          metadata: {
            kind: 'credit_request',
            workspaceId: wsId,
            needed: short.needed,
            available: short.available
          }
        })
      ).catch(() => null)
    )
  )
  if (!sent.some(Boolean)) {
    toast('error', t('workspace.credits.ask.failedTitle'), t('workspace.credits.ask.failedMessage'))
    return false
  }
  // Marked on the refusal it answers; a NEW refusal writes a fresh record.
  const held = r._creditsShort
  if (held && held.at === short.at) {
    r.update({ _creditsShort: { ...held, asked: true } }, { preventFetch: true })
  }
  toast('success', t('workspace.credits.ask.sentTitle'), t('workspace.credits.ask.sentMessage'))
  return true
}

// creditPillVisible(el, root) → is the credit warning pill actually ON SCREEN?
//
// THE ONE source of truth for that question, because TWO elements need it and
// they must never disagree: `CreditWarningPill` (whose `hide:` is its negation)
// and its `CreditPillDock` wrapper, which has to know whether it is carrying a
// visible pill before it may claim any geometry
// (WS-ASSISTANT-DRAWER-COVERED-BY-EMPTY-CREDIT-DOCK-1: the dock claimed the
// docked assistant's whole box off the dock attribute alone, so an EMPTY,
// opaque, z-index-10001 panel covered the entire AI thread on every workspace
// that is NOT out of credits — the common case).
//
// Self-contained per this file's FA204 convention: every dependency is reached
// through `el.call(...)` (the registry), never a sibling export or a
// module-scope helper — the frank lift extracts each export on its own.
// `el` is passed explicitly, the same shape `notifyCreditThreshold` uses,
// so a caller inside a nested factory can forward its own element.
export const creditPillVisible = function (el, root) {
  const e = el || this
  const r = root || e?.state?.root
  if (!e || !r || !r.signedIn) return false
  // White-label stand-down (mirrors CreditBlocker.Overlay): a NON-member
  // behind the request-access gate meets no credit chrome at all.
  if (e.call('isWhitelabelNonMemberGateActive', r)) return false
  // Scheme-A prefix stripped — match the route, not the tenancy prefix.
  const path = e.call('stripWorkspacePrefix', r.currentPath || '/') || '/'
  if (path.startsWith('/admin/usage')) return false
  // A held SHORTFALL (creditShortState) shows to every role — a member needs
  // the pointer to an admin as much as an admin needs the button — and does
  // not wait for the balance read: the refusal already said what the balance
  // was. It outranks the low/critical warning below; one pill, one message.
  if (e.call('creditShortState', e, r)) return true
  const guard = e.call('creditGuardState', r)
  if (!guard || !guard.loaded || guard.error) return false
  // EXHAUSTED belongs to CreditBlocker until that gate is dismissed; then this
  // pill takes over as the persistent indicator. Exactly one is ever visible.
  if (guard.status === 'exhausted') {
    if (!r._creditBlockerDismissed) return false
  } else if (guard.status !== 'low' && guard.status !== 'critical') {
    return false
  }
  if (!e.call('currentUserIsAdmin', r)) return false
  // Dismissal: highest dismissed level for the current month wins; an
  // ESCALATION re-shows. `_creditBannerDismissed` mirrors the pref into state
  // so this re-runs on dismiss without a reload.
  const wsId = r.workspace?.id || r.workspace?._id || 'ws'
  const period = new Date().toISOString().slice(0, 7)
  const level = guard.status === 'exhausted' ? 3 : guard.status === 'critical' ? 2 : 1
  const seen = r._creditBannerDismissed || e.call('getPref', `credit_banner_${wsId}`)
  const [seenPeriod, seenLevelRaw] = String(seen || '').split(':')
  if (seenPeriod === period && (Number(seenLevelRaw) || 0) >= level) return false
  return true
}

// creditDockMode(el, root) → 'cover' | 'pill' | null — what the dock carries.
//
// `null`  nothing: the pill is hidden, so the dock must claim NO geometry and
//         capture no pointer events (the defect above).
// `cover` the workspace is OUT OF CREDITS and the pill is up. Every metered
//         action in the assistant is already refused, so a docked drawer is a
//         dead surface and the notice takes its whole box — 2026-09-04 per
//         Nika "move 'Out of credits — metered actions paused' section above
//         ai chat zIndex on top of chatsidebar, make it beautiful and same
//         size as chatbot to perfectly override it (just in ui)". That quote
//         names the EXHAUSTED copy by its text
//         (`workspace.creditBanner.exhausted`), which is why the cover is
//         scoped to that one status here.
// `pill`  LOW or CRITICAL: the assistant still works, so the warning stays the
//         compact above-composer pill and never covers a live thread.
//
// Published as an attribute by the dock so the shell stylesheet can key on it
// (the same reacts-in-CSS idiom as <html data-assistant-dock>) instead of
// re-deriving credit state in CSS, which it cannot do.
export const creditDockMode = function (el, root) {
  const e = el || this
  const r = root || e?.state?.root
  if (!e || !e.call('creditPillVisible', e, r)) return null
  return e.call('creditGuardState', r)?.status === 'exhausted' ? 'cover' : 'pill'
}

// Fetch the live balance into `root._creditGuard`. Fire-and-forget safe:
// errors land as `{ error }` — the guard surfaces stay silent on error (a
// metering outage must never lock a workspace). `sdkMaybe` lets
// loadPrivateData / onWorkspaceChange pass their live client;
// el.call('loadCreditGuard') resolves one itself. Workspace id resolution
// mirrors the usage page (root.workspace lands via auth.js; jwtClaims lags
// on federation round-trips and stays null on sdk-only sessions).
export const loadCreditGuard = async function (root, sdkMaybe) {
  const r = root || this?.state?.root
  if (!r) return null
  const wsId = r?.workspace?.id || r?.workspace?._id || r?.jwtClaims?.active_workspace_id || null
  if (!wsId) return null
  const sdk = sdkMaybe || _wsShared.functions.getSDK()
  if (typeof sdk?.getCreditBalance !== 'function') return null
  try {
    const res = await sdk.getCreditBalance(wsId)
    const data = res?.data || res || {}
    r.update?.({ _creditGuard: { ...data, _wsId: wsId, _at: new Date().getTime() } })
  } catch (e) {
    // `error` is a FLAG — every guard surface stays silent on it (a metering
    // outage must never lock a workspace) — so its fallback is an id.
    r.update?.({ _creditGuard: { error: e?.message || 'balance_unavailable', _wsId: wsId } })
  }
  return r._creditGuard
}

// ── CreditBlocker dismissal persistence (sessionStorage, per-tab, per-
//    workspace) — CREDITS-GATE-UI-BREAK-1 finding 2's residual. ────────────
//
// `_creditBlockerDismissed` (state.js) is in-memory root state by design
// (CREDIT-BLOCK-1: "running out of credits is a hard fact, so a reload
// restates it once"). But the qa probe this ticket ran (tickets/sonnet.md,
// 2026-08-18 ~17:1x) measured that on dev the app REMOUNTS constantly from
// EXTERNAL causes — dev-server HMR/live-reload churn, confirmed by
// `navigation[0].type === 'reload'`, not an in-place app-level recreate —
// and every one of those remounts wipes root state, so the full-viewport
// gate re-armed on EVERY page mount (home, tickets, tasks…) all session,
// repeatedly eating clicks. A reload was never meant to be a NEW session.
//
// Fix shape mirrors shared/functions/minimizedSlots.js's per-workspace
// sessionStorage idiom exactly: own defensive `_storage()` accessor,
// wsId-stamped key, best-effort try/catch, never throws. sessionStorage
// (never localStorage) so the dismissal survives a reload/remount WITHIN
// the same tab but a genuinely new tab/session still sees the gate once —
// CREDIT-BLOCK-1's "hard fact restated once" intent, now scoped to "once
// per tab" instead of "once per render".
//
// Deliberately NOT `getPref`/`setPref` (shared/prefs.js, `smbls_pref_*`
// cookies, hardcoded one-year max-age, cross-app AND cross-tab by design):
// persisting there would outlive the tab and break "a new tab still shows
// it once" — the exact reason CREDIT-BLOCK-1 chose state over a pref
// cookie in the first place (state.js's `_creditBlockerDismissed` doc
// comment, CreditBlocker.js's header). That call stands; this is a
// narrower, still-session-scoped store layered UNDER it, not a
// replacement of it.
export const creditBlockerDismissedStorageKey = (wsId) => `credit_blocker_${wsId || 'ws'}`

export const readCreditBlockerDismissed = (wsId, storage) => {
  const _key = (id) => `credit_blocker_${id || 'ws'}`
  const _storage = (s) => {
    if (s) return s
    try {
      return globalThis.sessionStorage || null
    } catch (_) {
      return null
    }
  }
  const store = _storage(storage)
  if (!store || !wsId) return false
  try {
    return store.getItem(_key(wsId)) === '1'
  } catch (_) {
    return false
  }
}

export const writeCreditBlockerDismissed = (wsId, dismissed, storage) => {
  const _key = (id) => `credit_blocker_${id || 'ws'}`
  const _storage = (s) => {
    if (s) return s
    try {
      return globalThis.sessionStorage || null
    } catch (_) {
      return null
    }
  }
  const store = _storage(storage)
  if (!store || !wsId) return
  try {
    if (dismissed) store.setItem(_key(wsId), '1')
    else store.removeItem(_key(wsId))
  } catch (_) {
    /* best-effort — storage quota / privacy mode */
  }
}

// Threshold-crossing notifications, deduped per (workspace, month, level):
//   • toast (openNotification) for the CURRENT user when they are an admin —
//     local UI, so notifying yourself is fine (createNotification's
//     self-skip does not apply);
//   • persisted bell rows (createNotifications) for the OTHER admins.
// The dedupe pref stores the highest level already announced for the
// current month, so low → critical → exhausted each fire exactly once per
// month per client. Driven edge-style from CreditBanner/CreditBlocker
// renders (they see every `_creditGuard` change), NOT from the fetch — the
// fetch has no element to `el.call` through. This is the CLIENT half; the
// durable cross-client emission belongs in the server credit-meter
// (ticketed — an admin who never opens the app still needs the bell row).
export const notifyCreditThreshold = async function (el, root) {
  const host = el && el.call ? el : this
  const r = root || host?.state?.root
  if (!r || !host?.call) return
  // Inline status derivation (self-contained per the frank convention —
  // keep in sync with creditGuardState above).
  const raw = r?._creditGuard
  if (!raw) return
  const data = raw.data || raw
  if (data.error) return
  // UNKNOWN ≠ ZERO — same discriminator as creditGuardState above (kept in
  // sync deliberately; the frank lift extracts each export individually, so
  // this cannot call a shared helper). Announcing "you are out of credits"
  // off a balance we failed to read is the same false-confidence failure as
  // blocking on it, so an unreadable balance notifies nothing.
  const rawTotal = data.total
  const total = Number(rawTotal)
  if (data.found === false || rawTotal == null || rawTotal === '' || !Number.isFinite(total)) return
  const grantSize = Number(data.grantSize ?? 0)
  let status = 'ok'
  if (total <= 0) status = 'exhausted'
  else if (grantSize > 0 && total <= grantSize * 0.1) status = 'critical'
  else if (grantSize > 0 && total <= grantSize * 0.3) status = 'low'
  const level = status === 'exhausted' ? 3 : status === 'critical' ? 2 : status === 'low' ? 1 : 0
  if (level < 1) return

  let isAdminNow = false
  try {
    isAdminNow = !!host.call('currentUserIsAdmin', r)
  } catch (e) {}
  if (!isAdminNow) return

  // Dedupe key stability (CREDITS-GATE-UI-BREAK-1, the duplicate stacked
  // toasts): `r.workspace?.id` is a LATE-LANDING value — a `_creditGuard`
  // write can arrive (and this notifier fire) before `root.workspace`
  // resolves, so the first announcement deduped under `credit_notify_ws`
  // and the re-fire after the workspace landed deduped under
  // `credit_notify_<real id>` — two keys, two identical toasts. The guard
  // record itself carries the workspace it was FETCHED for (`_wsId`,
  // stamped by loadCreditGuard) — that key exists from the very first
  // fire and never changes shape mid-boot.
  const wsId = data._wsId || raw._wsId || r?.workspace?.id || r?.workspace?._id || 'ws'
  const period = new Date().toISOString().slice(0, 7)
  const prefKey = `credit_notify_${wsId}`
  // Prefs are COOKIES (shared/prefs.js) — objects don't survive the
  // round-trip, so the dedupe marker is a plain `period:level` string.
  let seen = null
  try {
    seen = host.call('getPref', prefKey)
  } catch (e) {}
  const [seenPeriod, seenLevelRaw] = String(seen || '').split(':')
  const seenLevel = seenPeriod === period ? Number(seenLevelRaw) || 0 : 0
  if (level <= seenLevel) return
  try {
    host.call('setPref', prefKey, `${period}:${level}`)
  } catch (e) {}

  const t = (key) => host.call('polyglot', key)
  const wsName = r?.workspace?.name || t('workspace.credits.thisWorkspace')
  const fmt = (n) => Number(n || 0).toLocaleString('en-US')
  const copy =
    status === 'exhausted'
      ? {
          title: t('workspace.credits.exhausted.title'),
          message: t('workspace.credits.exhausted.message').replace('{workspace}', wsName)
        }
      : status === 'critical'
        ? {
            title: t('workspace.credits.critical.title'),
            message: t('workspace.credits.critical.message')
              .replace('{workspace}', wsName)
              .replace('{count}', fmt(total))
          }
        : {
            title: t('workspace.credits.low.title'),
            message: t('workspace.credits.low.message')
              .replace('{workspace}', wsName)
              .replace('{count}', fmt(total))
          }

  try {
    host.call('openNotification', {
      type: status === 'low' ? 'warning' : 'error',
      title: copy.title,
      message: copy.message,
      href: '/admin/usage'
    })
  } catch (e) {}

  // Other admins: workspace people whose org role is owner/admin
  // (roles.byUser is keyed by userId; _loadRoles fills it from
  // listOrgMembers). createNotification itself refuses self-rows, and the
  // explicit filter keeps the fan-out honest anyway.
  const byUser = r?.roles?.byUser || {}
  const people = Array.isArray(r?.people) ? r.people : []
  const others = people
    .filter((p) => {
      const role = p?.userId != null ? byUser[p.userId] : null
      return role === 'owner' || role === 'admin'
    })
    .map((p) => p.email)
    .filter((email) => email && email !== r.currentUserEmail)
  if (others.length) {
    try {
      await host.call('createNotifications', others, {
        type: 'system',
        title: copy.title,
        body: copy.message,
        href: '/admin/usage',
        metadata: { kind: 'credit_threshold', level: status, workspaceId: wsId, period }
      })
    } catch (e) {}
  }
}

// The three normalized errorCode strings a credit-exhaustion throw can
// surface as (server: ConversationOrchestratorService.failWorkspaceLoop,
// mirroring CreditsService's InsufficientCreditsError /
// SpendCapExceededError / AllocationExceededError). One list so every
// consumer checks the SAME set instead of drifting per-caller (tickets/
// opus.md "credits-exhausted UX"). The server also stamps
// `metadata.creditsExhausted: true` for exactly these three codes — prefer
// that boolean where it's already present; this list is for callers (like
// notifyCreditsExhaustedFromMessage below) that want to double-check or that
// only have the bare errorCode.
export const CREDITS_EXHAUSTED_ERROR_CODES = [
  'INSUFFICIENT_CREDITS',
  'SPEND_CAP_EXCEEDED',
  'ALLOCATION_EXCEEDED'
]

// notifyCreditsExhaustedFromMessage — the credits-exhausted UX's SECOND
// notification driver (tickets/opus.md "credits-exhausted UX"). The first
// (notifyCreditThreshold above) reacts to the POLLED `_creditGuard` balance
// on an interval; this one reacts to a SPECIFIC failed chat turn the instant
// it lands (ChatMessage.js's onInit/onUpdate calls this with (el, s) where
// `s` is the message row's own state), so the notification appears without
// waiting for the next balance poll — and it also covers SPEND_CAP_EXCEEDED /
// ALLOCATION_EXCEEDED, which the balance-only guard can never detect (the
// workspace, or the org pool behind it, may still have a nonzero total).
//
// Role-aware per the ticket's table: admins get a notification pointing at
// /admin/usage AND an immediate balance refetch (which is what flips
// CreditBlocker — the zero-balance persistent banner — on right away instead
// of waiting for the next scheduled poll); non-admins get told to contact an
// administrator, with NO payment CTA they cannot action. `currentUserIsAdmin`
// is used deliberately (not the legacy `isAdmin`) — tickets/opus.md: "the
// exact same gate /admin/usage uses, so the CTA appears for precisely
// whoever can action it."
//
// Dedup'd per message id in root state — session-lifetime only (unlike
// notifyCreditThreshold's month-scoped pref): a reload re-announcing a
// still-broken workspace once is acceptable for a rare event tied to one
// specific failed message, and message ids are stable across a reload
// (sessions.js _toChatMessages restores the same `id`).
export const notifyCreditsExhaustedFromMessage = async function (el, s) {
  const host = el && el.call ? el : this
  if (!host || typeof host.call !== 'function') return
  const root = (s && s.root) || host.state?.root
  if (!root || !s || s.streaming) return
  const metadata = s.metadata
  if (!metadata || metadata.kind !== 'error') return
  const code = String(metadata.errorCode || '')
  const isCreditsExhausted =
    metadata.creditsExhausted === true ||
    ['INSUFFICIENT_CREDITS', 'SPEND_CAP_EXCEEDED', 'ALLOCATION_EXCEEDED'].includes(code)
  if (!isCreditsExhausted) return

  const msgId = String(s.id || '')
  const seenIds = root._creditsErrorNotifiedIds || []
  if (msgId && seenIds.includes(msgId)) return
  if (msgId) {
    root.update({ _creditsErrorNotifiedIds: [...seenIds, msgId] }, { preventFetch: true })
  }

  // HOLD the shortfall — the toast below is gone in seconds, and the reply in
  // the thread has no button (persona audit round 1: four of four new accounts
  // stopped at "needed 190-222, available 100" with no way on). CreditBanner
  // shows it, with the way out for this person's role, until the balance
  // covers it or they close it; /admin/usage reads it too. Only a BALANCE
  // refusal has a "needs N, has M" — a spend cap or an allocation does not.
  // The figures are the structured `creditsNeeded` / `creditsAvailable` (the
  // state layer writes them, creditsRefusal.js) or, on a row nothing
  // normalized, the server's own "needed N, available M". Inline on purpose:
  // this file's exports are self-contained (the frank lift, above).
  if (code !== 'SPEND_CAP_EXCEEDED' && code !== 'ALLOCATION_EXCEEDED') {
    const count = (v) => {
      if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
      const n = Number(v)
      return Number.isFinite(n) && n >= 0 ? n : null
    }
    let needed = count(metadata.creditsNeeded)
    let available = count(metadata.creditsAvailable)
    if (needed === null || available === null) {
      const hit = /needed\s+(\d+(?:\.\d+)?)\s*,\s*available\s+(\d+(?:\.\d+)?)/i.exec(
        String(metadata.errorMessage || '')
      )
      needed = hit ? Number(hit[1]) : null
      available = hit ? Number(hit[2]) : null
    }
    if (needed !== null && available !== null) {
      const at = Number(s.ts) || new Date().getTime()
      const held = root._creditsShort
      root.update(
        {
          _creditsShort: {
            needed,
            available,
            at,
            msgId,
            // The same refusal told twice (a live row re-keyed to its server
            // id) keeps its "closed" and its "admins told"; a NEW refusal
            // opens — and asks — again.
            dismissed: !!(held && held.at === at && held.dismissed),
            asked: !!(held && held.at === at && held.asked)
          }
        },
        { preventFetch: true }
      )
    }
  }

  let isAdminNow = false
  try {
    isAdminNow = !!host.call('currentUserIsAdmin', root)
  } catch (e) {}

  if (!isAdminNow) {
    try {
      host.call('openNotification', {
        type: 'error',
        title: host.call('polyglot', 'workspace.credits.member.title'),
        message: host.call('polyglot', 'workspace.credits.member.message')
      })
    } catch (e) {}
    return
  }

  const t = (key) => host.call('polyglot', key)
  const copy =
    code === 'SPEND_CAP_EXCEEDED'
      ? {
          title: t('workspace.credits.spendCap.title'),
          message: t('workspace.credits.spendCap.message')
        }
      : code === 'ALLOCATION_EXCEEDED'
        ? {
            title: t('workspace.credits.allocation.title'),
            message: t('workspace.credits.allocation.message')
          }
        : {
            title: t('workspace.credits.blocked.title'),
            message: t('workspace.credits.blocked.message')
          }

  try {
    host.call('openNotification', {
      type: 'error',
      title: copy.title,
      message: copy.message,
      href: '/admin/usage'
    })
  } catch (e) {}

  // Refresh the balance NOW so CreditBlocker/CreditBanner flip on
  // immediately instead of waiting for the next scheduled poll — matters
  // most for INSUFFICIENT_CREDITS (an actual zero balance). Spend-cap /
  // allocation hits don't necessarily zero the balance, so the guard may
  // legitimately stay quiet for those; the notification above still fired
  // either way.
  try {
    await host.call('loadCreditGuard', root)
  } catch (e) {}
}
